import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Link as RouterLink } from "react-router-dom";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { AccountManagementStore } from "../../../stores/AccountManagementStore";

const DRAWER_Z_OFFSET = 2000;

interface AccountQuarterOrdersDrawerProps {
  store: AccountManagementStore;
}

const formatDisplayDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const formatted = formatDateWithoutTime(value);
  return formatted || value || "-";
};

const formatCurrency = (value: number | null | undefined) => {
  try {
    if (value == null || !Number.isFinite(value)) return "-";
    return `₹${value.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const AccountQuarterOrdersDrawer: React.FC<AccountQuarterOrdersDrawerProps> = ({
  store,
}) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const customColors = theme.palette.customColors;
  const open = store.quarterDrawerOpen;
  const context = store.quarterDrawerContext;
  const result = store.quarterOrders;

  useEffect(() => {
    if (!open || !context) return;

    const load = async () => {
      try {
        await store.fetchQuarterOrders();
      } catch (error: any) {
        showError(
          "Error",
          error?.message || "Failed to fetch quarter orders",
        );
      }
    };

    void load();
  }, [
    open,
    context?.customer_id,
    context?.fy_year,
    context?.quarter,
    showError,
    store,
  ]);

  const summary = useMemo(() => {
    return {
      totalOrders: result?.total_orders ?? 0,
      totalNetAmount: result?.total_net_amount ?? 0,
      period:
        result?.start_date && result?.end_date
          ? `${formatDisplayDate(result.start_date)} – ${formatDisplayDate(result.end_date)}`
          : "-",
    };
  }, [result]);

  if (!context) return null;

  const orders = result?.orders ?? [];
  const quarterLabel = `FY${context.fy_year} Q${context.quarter}`;

  const summaryCards = [
    {
      label: "Total Orders",
      value: String(summary.totalOrders),
      accent: customColors.darkBlue,
    },
    {
      label: "Total Net Amount",
      value: formatCurrency(summary.totalNetAmount),
      accent: customColors.purpleMain,
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => store.closeQuarterDrawer()}
      sx={{ zIndex: theme.zIndex.drawer + DRAWER_Z_OFFSET }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 620, md: 720 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: customColors.pageBackground,
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          borderBottom: `1px solid ${customColors.softBorder}`,
          bgcolor: customColors.white,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: customColors.lightBlue,
              color: customColors.darkBlue,
            }}
          >
            <ReceiptLongOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <CustomTypography
                variant="heading5"
                sx={{ color: customColors.navyText, wordBreak: "break-word" }}
              >
                {context.account_name}
              </CustomTypography>
              <Chip
                size="small"
                label={quarterLabel}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: `${customColors.purpleMain}14`,
                  color: customColors.purpleMain,
                  border: `1px solid ${customColors.purpleMain}33`,
                }}
              />
            </Box>
            <CustomTypography
              variant="body4"
              sx={{ mt: 0.5, color: customColors.mutedSlateText }}
            >
              Period: {summary.period}
            </CustomTypography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => store.closeQuarterDrawer()}
          aria-label="Close quarter orders drawer"
          sx={{
            color: customColors.mutedSlateText,
            bgcolor: customColors.lightGreyColor,
            "&:hover": { bgcolor: customColors.softBorder },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          overflow: "auto",
          px: { xs: 2, sm: 2.5 },
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <LoaderOverlay loading={store.quarterDrawerLoading} />

        <Grid2 container spacing={1.5}>
          {summaryCards.map((card) => (
            <Grid2 key={card.label} size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: 2.5,
                  bgcolor: customColors.white,
                  border: `1px solid ${customColors.softCardBorder}`,
                  borderLeft: `4px solid ${card.accent}`,
                  boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                }}
              >
                <CustomTypography
                  variant="body4"
                  sx={{
                    color: customColors.mutedSlateText,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  {card.label}
                </CustomTypography>
                <CustomTypography
                  variant="heading4"
                  sx={{
                    mt: 0.75,
                    color: customColors.navyText,
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    wordBreak: "break-word",
                  }}
                >
                  {card.value}
                </CustomTypography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            bgcolor: customColors.white,
            border: `1px solid ${customColors.softCardBorder}`,
            overflow: "hidden",
            boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              bgcolor: customColors.white,
            }}
          >
            <CustomTypography
              variant="heading7"
              sx={{ color: customColors.navyText, fontWeight: 700 }}
            >
              Orders
            </CustomTypography>
            <CustomTypography
              variant="body4"
              sx={{ color: customColors.mutedSlateText }}
            >
              {summary.totalOrders}{" "}
              {summary.totalOrders === 1 ? "order" : "orders"}
            </CustomTypography>
          </Box>
          <Divider sx={{ borderColor: customColors.softBorder }} />

          {orders.length === 0 && !store.quarterDrawerLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                px: 2,
              }}
            >
              <CustomTypography
                variant="body3"
                sx={{ color: customColors.mutedSlateText }}
              >
                No orders found for this quarter.
              </CustomTypography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "62vh" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Order ID", align: "left" as const, width: "100px" },
                      { label: "Label", align: "left" as const, width: "160px" },
                      { label: "Client POC", align: "left" as const, width: "140px" },
                      { label: "Date", align: "center" as const, width: "110px" },
                      { label: "Net Amount", align: "right" as const, width: "130px" },
                    ].map((column) => (
                      <AppTableHeaderCell
                        key={column.label}
                        minWidth={column.width}
                        txtAlign={column.align}
                        fontColor={customColors.navyText}
                        fontWeight={600}
                        sx={{
                          fontSize: "0.72rem",
                          py: 1.25,
                          px: 1.5,
                          bgcolor: "#EEF1F8",
                          borderBottom: `1px solid ${customColors.softBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </AppTableHeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order, index) => (
                    <TableRow
                      key={order.order_id}
                      sx={{
                        bgcolor:
                          index % 2 === 0
                            ? customColors.white
                            : customColors.pageBackground,
                        "&:hover": {
                          bgcolor: customColors.lightBlue,
                        },
                        "& td": {
                          borderBottom: `1px solid ${customColors.softBorder}`,
                        },
                      }}
                    >
                      <AppTableRowCell
                        textAlign="left"
                        align="left"
                        minWidth="100px"
                        fontWeight={600}
                        fontSize="0.78rem"
                        color={customColors.navyText}
                        sx={{ py: 1.15, px: 1.5 }}
                      >
                        <Box
                          component={RouterLink}
                          to={`/orders/add-orders?orderId=${order.order_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          sx={{
                            display: "inline-flex",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: customColors.lightBlue,
                            color: customColors.darkBlue,
                            fontWeight: 700,
                            fontSize: "0.74rem",
                            textDecoration: "none",
                            "&:hover": {
                              bgcolor: customColors.purpleMain,
                              color: customColors.white,
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {order.order_id}
                        </Box>
                      </AppTableRowCell>
                      <AppTableRowCell
                        textAlign="left"
                        align="left"
                        minWidth="160px"
                        fontWeight={500}
                        fontSize="0.78rem"
                        color={
                          order.label
                            ? customColors.navyText
                            : customColors.mutedSlateText
                        }
                        sx={{ py: 1.15, px: 1.5 }}
                      >
                        {order.label || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        textAlign="left"
                        align="left"
                        minWidth="140px"
                        fontWeight={500}
                        fontSize="0.78rem"
                        color={
                          order.client_poc
                            ? customColors.navyText
                            : customColors.mutedSlateText
                        }
                        sx={{ py: 1.15, px: 1.5 }}
                      >
                        {order.client_poc || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        textAlign="center"
                        align="center"
                        minWidth="110px"
                        fontWeight={500}
                        fontSize="0.78rem"
                        color={customColors.navyText}
                        sx={{ py: 1.15, px: 1.5, whiteSpace: "nowrap" }}
                      >
                        {formatDisplayDate(order.date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        textAlign="right"
                        align="right"
                        minWidth="130px"
                        fontWeight={700}
                        fontSize="0.8rem"
                        color={customColors.navyText}
                        sx={{
                          py: 1.15,
                          px: 1.5,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatCurrency(order.net_amount)}
                      </AppTableRowCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Drawer>
  );
};

export default observer(AccountQuarterOrdersDrawer);
